import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import PricingTierCard from './components/PricingTierCard';
import FreePlanHighlight from './components/FreePlanHighlight';
import ComparisonTable from './components/ComparisonTable';
import FAQSection from './components/FAQSection';
import Footer from '../homepage-sales-landing/components/Footer';

const PricingPage = () => {
  const navigate = useNavigate();

  const pricingTiers = [
    {
      id: 'free',
      name: 'Free Plan',
      price: 0,
      description: 'Research and plan your book',
      icon: 'Gift',
      color: 'accent',
      features: [
        'Access to Agents 1-3',
        'Unlimited topic research',
        'Writing voice capture',
        'Unlimited outline creation',
        'Book project management'
      ],
      limitations: [
        'No AI manuscript writing',
        'No cover design generation',
        'No KDP publishing features',
        'No community access',
        'No PDF export (upgrade required)'
      ],
      upgradeNote: 'Upgrade to any paid plan to start writing your book with AI'
    },
    {
      id: 'starter',
      name: 'Starter',
      price: 49,
      books: 1,
      description: 'Perfect for your first book',
      icon: 'BookOpen',
      color: 'primary',
      features: [
        'Create 1 complete book per month',
        'All 11 AI agents included',
        'Writing voice capture',
        'Print & ebook formatting',
        'Cover design generation',
        'KDP metadata creation',
        'Access to Authorpreneur Community',
        'Email support'
      ],
      limitations: []
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 99,
      books: 4,
      description: 'Most popular for serious authors',
      icon: 'Crown',
      color: 'secondary',
      popular: true,
      features: [
        'Create 4 complete books per month',
        'All 11 AI agents included',
        'Priority AI processing',
        'Advanced voice analysis',
        'Premium cover designs',
        'SEO-optimized metadata',
        'Access to Authorpreneur Community',
        'Priority email support',
        'Monthly strategy calls'
      ],
      limitations: []
    },
    {
      id: 'business',
      name: 'Business',
      price: 199,
      books: 10,
      description: 'For prolific content creators',
      icon: 'Briefcase',
      color: 'accent',
      features: [
        'Create 10 complete books per month',
        'All 11 AI agents included',
        'Fastest AI processing',
        'Multi-voice profiles',
        'Custom cover templates',
        'Advanced market research',
        'Access to Authorpreneur Community',
        'Priority support (24h)',
        'Weekly strategy calls',
        'Bulk export options'
      ],
      limitations: []
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 999,
      books: 'Unlimited',
      description: 'For agencies and publishers',
      icon: 'Building',
      color: 'success',
      features: [
        'Unlimited book creation',
        'All 11 AI agents included',
        'Dedicated AI resources',
        'Team collaboration tools',
        'White-label options',
        'API access',
        'Access to Authorpreneur Community',
        'Dedicated account manager',
        'Custom integrations',
        'Training & onboarding'
      ],
      limitations: []
    }
  ];

  const handleSelectPlan = (tierId) => {
    if (tierId === 'free') {
      navigate('/user-registration?plan=free');
    } else {
      localStorage.setItem('selectedTier', tierId);
      navigate('/user-registration');
    }
  };

  return (
    <>
      <Helmet>
        <title>Pricing Plans - AuthorPreneur AI | Free Research & Paid Writing Plans</title>
        <meta
          name="description"
          content="Start free with research and outline creation, or choose a paid plan to unlock AI-powered book writing. Plans from $49/month with all 11 AI agents included."
        />
        <meta name="keywords" content="book writing pricing, AI author tools, free book planning, paid writing software" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header Navigation */}
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="BookOpen" size={24} color="var(--color-primary)" />
                </div>
                <span className="text-xl font-heading font-semibold text-foreground">
                  AuthorPreneur AI
                </span>
              </button>

              <nav className="hidden md:flex items-center space-x-6">
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Home
                </button>
                <button
                  onClick={() => navigate('/user-login')}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Sign In
                </button>
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-20 pb-12 bg-gradient-to-br from-muted/30 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-6">
                <Icon name="DollarSign" size={16} color="var(--color-accent)" />
                <span className="text-sm font-medium text-accent">Simple, Transparent Pricing</span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-heading font-bold text-foreground mb-6">
                Start Free, Upgrade When Ready
              </h1>

              <p className="text-xl text-muted-foreground mb-8">
                Research and plan your book for free. Upgrade to unlock AI-powered writing and complete your book faster than ever.
              </p>
            </div>

            <FreePlanHighlight />
          </div>
        </section>

        {/* Pricing Tiers */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                Choose Your Plan
              </h2>
              <p className="text-lg text-muted-foreground">
                All paid plans include access to 11 AI agents and Authorpreneur Community
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
              {pricingTiers?.map((tier) => (
                <PricingTierCard
                  key={tier?.id}
                  tier={tier}
                  onSelectPlan={handleSelectPlan}
                  featured={tier?.popular}
                />
              ))}
            </div>

            {/* Feature Comparison */}
            <div className="mb-16">
              <div className="text-center mb-8">
                <h2 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-3">
                  Compare All Features
                </h2>
                <p className="text-muted-foreground">
                  Detailed breakdown of what's included in each plan
                </p>
              </div>
              <ComparisonTable />
            </div>

            {/* Community Access Notice */}
            <div className="bg-card border border-border rounded-xl p-8 mb-16">
              <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={24} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                      Access to Authorpreneur Community
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      All paid members get instant access to our exclusive community of authors, entrepreneurs, and publishing experts on Skool.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <FAQSection />

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-12 text-center mt-16">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
                Ready to Write Your Book?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
                Start with our Free Plan to research and outline your book. Upgrade anytime to unlock AI-powered writing.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => handleSelectPlan('free')}
                  className="px-8 py-4 bg-white text-primary font-semibold rounded-lg hover:bg-white/90 transition-colors shadow-lg"
                >
                  Start Free Now
                </button>
                <button
                  onClick={() => handleSelectPlan('professional')}
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border-2 border-white/30"
                >
                  View Professional Plan
                </button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PricingPage;